/**
 * Footer — site footer with CTA band, navigation, socials and CV download.
 *
 * Renders a closing call-to-action, anchor links from NAV, social links from
 * SOCIALS, and the bottom copyright bar. Entrance animation is gated by
 * useGlobalReveal.
 */
import "../ComponentsStyling/Footer.css";
import { useGlobalReveal } from "../utils/useGlobalReveal";
import { IconEmail, IconLinkedIn, IconGitHub } from "./ChannelIcons";

// Footer navigation anchors mirroring the page sections.
const NAV = [
    { label: "About", href: "#about" },
    { label: "Skills", href: "#skills" },
    { label: "Projects", href: "#work" },
    { label: "Worked With", href: "#workedwith" },
    // Certificates section is not live yet — kept as a placeholder for when it ships.
    // { label: "Certificates", href: "#certificates" },
    { label: "Contact", href: "#contact" },
];

/* Social/contact links rendered in the footer.

   These were a FIFTH hand-drawn set of the same three destinations — an
   outline envelope, a LinkedIn approximation assembled from a path, a rect and
   a circle, and a monoline octocat — none of which matched the marks the
   header and Contact use. They come from the shared ChannelIcons module now,
   at footer scale. */
const SOCIALS = [
    {
        label: "Email",
        href: "mailto:khaledraedwork@gmail.com",
        icon: <IconEmail size={17} />,
    },
    {
        label: "LinkedIn",
        href: "https://www.linkedin.com/in/khaled-raed-0a92412a7/",
        icon: <IconLinkedIn size={14} />,
    },
    {
        label: "GitHub",
        href: "https://github.com/KhaledRaed",
        icon: <IconGitHub size={16} />,
    },
];
export default function Footer() {
    const [ref, inView] = useGlobalReveal({ threshold: 0.13, once: true });
    return (
        <footer className={`ft${inView ? " ft--in" : ""}`} ref={ref}>
            <div className="ft__dots" aria-hidden="true" />
            <div className="ft__blob--a" aria-hidden="true" />
            <div className="ft__blob--b" aria-hidden="true" />

            <div className="ft__rule" aria-hidden="true" />

            {/* Call-to-action band */}
            <div className="ft__cta">
                <div className="ft__cta-left">
                    <span className="avail ft__avail">Available for freelance</span>
                    <h2 className="ft__cta-h">
                        Let's build something
                        <em className="ft__cta-em"> great together.</em>
                    </h2>
                </div>
                <a href="#contact" className="btn btn--primary btn--lg">
                    Get in Touch
                    <svg className="btn__icon btn__icon--arrow" viewBox="0 0 16 16"
                        fill="none" aria-hidden="true">
                        <path d="M3 8h10M9 4l4 4-4 4"
                            stroke="currentColor" strokeWidth="1.7"
                            strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </a>
            </div>
            <div className="ft__sep" aria-hidden="true" />
            {/* Three-column body: identity, navigation, connect */}
            <div className="ft__body">
                {/* Identity: name, role and location */}
                <div className="ft__identity">
                    <span className="ft__name">Khaled Raed</span>
                    <span className="ft__role">Frontend Developer · Shabakkat KSA</span>
                    <span className="ft__location">
                        <svg viewBox="0 0 14 16" fill="none" aria-hidden="true">
                            <path d="M7 1a5 5 0 0 1 5 5c0 4-5 9-5 9S2 10 2 6a5 5 0 0 1 5-5z"
                                stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                            <circle cx="7" cy="6" r="1.5" stroke="currentColor" strokeWidth="1.4" />
                        </svg>
                        Amman, Jordan
                    </span>
                </div>
                {/* Section navigation */}
                <nav className="ft__nav" aria-label="Footer navigation">
                    <span className="ft__col-label">Navigation</span>
                    {NAV.map(l => (
                        <a key={l.href} href={l.href} className="ft__nav-link">
                            {l.label}
                        </a>
                    ))}
                </nav>
                {/* Social links and CV download */}
                <div className="ft__right">
                    <span className="ft__col-label">Connect</span>
                    <div className="ft__socials">
                        {/* mailto: opened in a new tab strands an empty tab once
                            the mail client takes over — only http goes external. */}
                        {SOCIALS.map(s => (
                            <a
                                key={s.label}
                                href={s.href}
                                {...(s.href.startsWith("http")
                                    ? { target: "_blank", rel: "noopener noreferrer" }
                                    : {})}
                                className="ft__social"
                                aria-label={s.label}
                            >
                                <span className="ft__social-icon">{s.icon}</span>
                                <span className="ft__social-label">{s.label}</span>
                            </a>
                        ))}
                    </div>
                    <a
                        href="/CV.pdf"
                        download
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn--secondary ft__cv"
                    >
                        <svg className="btn__icon btn__icon--down" viewBox="0 0 18 18"
                            fill="none" aria-hidden="true">
                            <path d="M9 3v8M6 8l3 3 3-3M4 14h10"
                                stroke="currentColor" strokeWidth="1.7"
                                strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        Download CV
                    </a>
                </div>
            </div>
            {/* Bottom bar: copyright and credit */}
            <div className="ft__bottom">
                <span className="ft__copy">
                    © {new Date().getFullYear()} Khaled Raed. All rights reserved.
                </span>
                <span className="ft__bottom-dot" aria-hidden="true" />
                <span className="ft__made">Designed &amp; built by Khaled Raed</span>
            </div>
        </footer>
    );
}